import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  Mail,
  Pencil,
  Printer,
  Trash2,
  X,
  ExternalLink,
  Link2,
  Phone,
  CreditCard,
  CheckCircle,
  Send,
  XCircle,
  Loader2,
  RotateCcw,
  Eraser,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { emailPoToSupplier } from '@/lib/emailPo';
import { printPdf } from '@/lib/printPdf';
import { useAuthContext } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatCents, formatDate, todayISO } from '@/lib/utils';
import AttachmentsPanel from '@/components/AttachmentsPanel';
import EmailPdfModal from '@/components/EmailPdfModal';
import { LinkEstimateToPoModal } from '@/components/LinkEstimateToPoModal';
import { WRITE_OFF_CAP_CENTS } from '@billflow/shared';
import type { POStatus, Supplier } from '@billflow/shared';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PoItem {
  id: number;
  item_id: number | null;
  description: string;
  qty: number;
  unit_price: number;
  sqft_per_box: number | null;
  sqft: number | null;
  line_total: number;
  sort_order: number;
  item_name: string | null;
  category: string | null;
  unit: string | null;
}

interface PoPayment {
  id: number;
  po_id: number;
  amount: number;
  date: string;
  notes: string | null;
  created_by: number | null;
  created_at: string;
}

interface PurchaseOrderDetail {
  id: number;
  number: string;
  supplier_id: number;
  source_estimate_id: number | null;
  order_date: string;
  expected_date: string | null;
  status: POStatus;
  subtotal: number;
  total: number;
  write_off: number;
  amount_paid: number;
  notes: string | null;
}

interface PurchaseReturnSummary {
  id: number;
  number: string;
  status: string;
  total: number;
  date: string;
}

interface PoDetailResponse {
  data: {
    purchase_order: PurchaseOrderDetail;
    items: PoItem[];
    supplier: Supplier;
    payments: PoPayment[];
    source_estimate: { id: number; number: string } | null;
    purchase_returns: PurchaseReturnSummary[];
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusBadge(status: POStatus) {
  switch (status) {
    case 'draft':
      return <Badge variant="gray">Draft</Badge>;
    case 'sent':
      return <Badge variant="blue">Sent</Badge>;
    case 'received':
      return <Badge variant="green">Received</Badge>;
    case 'returned':
      return <Badge variant="amber">Returned</Badge>;
    case 'cancelled':
      return <Badge variant="red">Cancelled</Badge>;
    default:
      return <Badge variant="gray">{status}</Badge>;
  }
}

// ─── Record PO Payment Modal ───────────────────────────────────────────────────

interface RecordPoPaymentModalProps {
  poId: number;
  supplierId: number;
  balanceDue: number;
  onClose: () => void;
  onSaved: () => void;
}

function RecordPoPaymentModal({
  poId,
  supplierId,
  balanceDue,
  onClose,
  onSaved,
}: RecordPoPaymentModalProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const [amountStr, setAmountStr] = useState(String((Math.max(balanceDue, 0) / 100).toFixed(2)));
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState('');
  const [writeOff, setWriteOff] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // PO payments are cash-only (no store credit), so the remainder is simply
  // whatever the cash amount leaves on the balance.
  const cashCents = Math.round((parseFloat(amountStr) || 0) * 100);
  const projectedRemaining = balanceDue - cashCents;
  // A PO payment can never exceed the balance due — overpayment is rejected
  // server-side (no auto-credit). Block it here too so the button can't fire it.
  const overpaying = cashCents > balanceDue;
  const showWriteOff =
    isAdmin && projectedRemaining > 0 && projectedRemaining <= WRITE_OFF_CAP_CENTS;

  // If the amount changes and the row hides, don't silently carry a stale "checked" forward.
  useEffect(() => {
    if (!showWriteOff) setWriteOff(false);
  }, [showWriteOff]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amount = cashCents;
    if (amount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    if (amount > balanceDue) {
      setError(`Amount exceeds the balance due of ${formatCents(balanceDue)}.`);
      return;
    }
    setLoading(true);
    try {
      await apiFetch('/api/po-payments', {
        method: 'POST',
        body: JSON.stringify({
          po_id: poId,
          amount,
          date,
          notes: notes.trim() || undefined,
          write_off_remainder: showWriteOff && writeOff ? true : undefined,
        }),
      });
      toast.success('Payment recorded');
      queryClient.invalidateQueries({ queryKey: ['po-payments'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-ledger', String(supplierId)] });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Record Payment</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="pp-amount">
                Amount ($) <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  $
                </span>
                <Input
                  id="pp-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  className="pl-7 tabular-nums"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pp-date">Date</Label>
              <Input
                id="pp-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pp-notes">Notes (optional)</Label>
            <Input
              id="pp-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Cash payment"
              disabled={loading}
            />
          </div>
          {balanceDue > 0 && (
            <p className="text-xs text-muted-foreground">
              Balance due:{' '}
              <span className="font-medium tabular-nums">{formatCents(balanceDue)}</span>
            </p>
          )}
          {overpaying && (
            <p className="text-sm text-destructive">
              Amount exceeds the balance due of {formatCents(balanceDue)}. Reduce the amount, or
              issue supplier credit separately.
            </p>
          )}
          {showWriteOff && (
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
              <input
                type="checkbox"
                checked={writeOff}
                onChange={(e) => setWriteOff(e.target.checked)}
                disabled={loading}
                className="h-4 w-4 rounded border-input"
              />
              Write off remaining {formatCents(projectedRemaining)} (clear the balance)
            </label>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || overpaying}>
              {loading ? 'Saving…' : 'Record Payment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Edit PO Payment Modal ─────────────────────────────────────────────────────

interface EditPoPaymentModalProps {
  payment: PoPayment;
  supplierId: number;
  onClose: () => void;
  onSaved: () => void;
}

function EditPoPaymentModal({ payment, supplierId, onClose, onSaved }: EditPoPaymentModalProps) {
  const queryClient = useQueryClient();
  const [amountStr, setAmountStr] = useState(String((payment.amount / 100).toFixed(2)));
  const [date, setDate] = useState(payment.date.slice(0, 10));
  const [notes, setNotes] = useState(payment.notes ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amount = Math.round((parseFloat(amountStr) || 0) * 100);
    if (amount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/po-payments/${payment.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          amount,
          date,
          notes: notes.trim() || undefined,
        }),
      });
      toast.success('Payment updated');
      queryClient.invalidateQueries({ queryKey: ['po-payments'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-ledger', String(supplierId)] });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Edit Payment</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="epp-amount">
                Amount ($) <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  $
                </span>
                <Input
                  id="epp-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  className="pl-7 tabular-nums"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="epp-date">Date</Label>
              <Input
                id="epp-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="epp-notes">Notes (optional)</Label>
            <Input
              id="epp-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Cash payment"
              disabled={loading}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel?: string;
  confirmVariant?: 'destructive' | 'default';
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  confirmVariant = 'destructive',
  onConfirm,
  onCancel,
  loading,
}: ConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">{message}</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant={confirmVariant} onClick={onConfirm} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Processing…
                </>
              ) : (
                confirmLabel
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Modal state ──
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [editPayment, setEditPayment] = useState<PoPayment | null>(null);
  const [deletePaymentId, setDeletePaymentId] = useState<number | null>(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [deletePOOpen, setDeletePOOpen] = useState(false);
  const [deletingPO, setDeletingPO] = useState(false);
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [writingOff, setWritingOff] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [linkEstimateOpen, setLinkEstimateOpen] = useState(false);
  const [lifecycleLoading, setLifecycleLoading] = useState<string | null>(null);
  const [confirmLifecycle, setConfirmLifecycle] = useState<{
    action: 'send' | 'receive' | 'cancel';
    label: string;
    message: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['purchase-order', id],
    queryFn: () => apiFetch<PoDetailResponse>(`/api/purchase-orders/${id}`),
    enabled: !!id,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
    queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
  }

  // ── Lifecycle action ──
  async function handleLifecycle(action: 'send' | 'receive' | 'cancel') {
    setLifecycleLoading(action);
    try {
      await apiFetch(`/api/purchase-orders/${id}/${action}`, { method: 'POST' });
      invalidate();
      if (po) {
        queryClient.invalidateQueries({ queryKey: ['supplier-ledger', String(po.supplier_id)] });
      }
      if (action === 'send') {
        // Same as the form's "Save & Send": email the PO PDF to the supplier (warns, never throws).
        await emailPoToSupplier(Number(id));
      } else {
        const labels = { receive: 'Marked as Received', cancel: 'Cancelled' };
        toast.success(labels[action]);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setLifecycleLoading(null);
      setConfirmLifecycle(null);
    }
  }

  // ── Write off remaining balance (admin, small-balance close-out) ──
  async function handleWriteOff() {
    setWritingOff(true);
    try {
      await apiFetch(`/api/purchase-orders/${id}/write-off`, { method: 'POST' });
      toast.success('Balance written off');
      invalidate();
      if (po) {
        queryClient.invalidateQueries({ queryKey: ['supplier-ledger', String(po.supplier_id)] });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to write off balance');
    } finally {
      setWritingOff(false);
      setWriteOffOpen(false);
    }
  }

  // ── Delete PO ──
  async function handleDeletePO() {
    setDeletingPO(true);
    try {
      await apiFetch(`/api/purchase-orders/${id}`, { method: 'DELETE' });
      toast.success('Purchase order deleted');
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      navigate('/purchase-orders');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeletingPO(false);
      setDeletePOOpen(false);
    }
  }

  // ── Delete payment ──
  async function handleDeletePayment(paymentId: number) {
    setDeletingPayment(true);
    try {
      await apiFetch(`/api/po-payments/${paymentId}`, { method: 'DELETE' });
      toast.success('Payment deleted');
      queryClient.invalidateQueries({ queryKey: ['po-payments'] });
      if (po) {
        queryClient.invalidateQueries({ queryKey: ['supplier-ledger', String(po.supplier_id)] });
      }
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete payment');
    } finally {
      setDeletingPayment(false);
      setDeletePaymentId(null);
    }
  }

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  if (error || !data?.data.purchase_order) {
    return (
      <div className="p-6">
        <Link
          to="/purchase-orders"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Purchase Orders
        </Link>
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Purchase order not found.'}
        </div>
      </div>
    );
  }

  const {
    purchase_order: po,
    items,
    supplier,
    payments,
    source_estimate,
    purchase_returns,
  } = data.data;
  const balanceDue = po.total - po.amount_paid;
  // POs have no "paid" status, so gate on the remaining balance: once fully paid
  // there is nothing left to record (overpayment is rejected), so hide the button.
  const canRecordPayment =
    (po.status === 'sent' || po.status === 'received' || po.status === 'returned') &&
    balanceDue > 0;
  const canEdit = po.status === 'draft' || (isAdmin && po.status === 'sent');

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* ── Back nav ── */}
      <Link
        to="/purchase-orders"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Purchase Orders
      </Link>

      {/* ── Body: 60/40 grid — header + line items in the 60% main column ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Main column (60%): header, line items + totals, payments */}
        <div className="lg:col-span-3 space-y-6">
          {/* ── Header card ── */}
          <Card>
            <div className="p-6 pb-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                {/* Left: number, status, supplier, dates */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl font-bold tracking-tight tabular-nums">{po.number}</h1>
                    {statusBadge(po.status)}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm">
                    <Link
                      to={`/suppliers/${supplier.id}`}
                      className="font-medium text-foreground hover:underline inline-flex items-center gap-1"
                    >
                      {supplier.name}
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </Link>
                    {supplier.phone && (
                      <>
                        <span className="hidden sm:inline text-muted-foreground">·</span>
                        <a
                          href={`tel:${supplier.phone}`}
                          className="text-muted-foreground hover:text-foreground hover:underline inline-flex items-center gap-1"
                        >
                          <Phone className="h-3 w-3" />
                          {supplier.phone}
                        </a>
                      </>
                    )}
                    <span className="hidden sm:inline text-muted-foreground">·</span>
                    <span className="text-muted-foreground">
                      Ordered {formatDate(po.order_date)}
                    </span>
                    {po.expected_date && (
                      <>
                        <span className="hidden sm:inline text-muted-foreground">·</span>
                        <span className="text-muted-foreground">
                          Expected {formatDate(po.expected_date)}
                        </span>
                      </>
                    )}
                  </div>
                  {source_estimate ? (
                    <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span>
                        From estimate:{' '}
                        <Link
                          to={`/estimates/${source_estimate.id}`}
                          className="font-medium text-foreground hover:underline inline-flex items-center gap-1"
                        >
                          {source_estimate.number}
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </Link>
                      </span>
                      <button
                        type="button"
                        onClick={() => setLinkEstimateOpen(true)}
                        className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                      >
                        Change
                      </button>
                      <button
                        type="button"
                        onClick={() => setLinkEstimateOpen(true)}
                        className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                      >
                        Unlink
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setLinkEstimateOpen(true)}
                      className="text-sm text-muted-foreground hover:text-foreground hover:underline inline-flex items-center gap-1.5 w-fit"
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      Link estimate
                    </button>
                  )}
                  {po.notes && (
                    <p className="text-sm text-muted-foreground max-w-prose whitespace-pre-line">
                      {po.notes}
                    </p>
                  )}
                </div>

                {/* Right: action buttons */}
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => printPdf(`/api/purchase-orders/${id}/pdf`)}
                  >
                    <Printer className="h-3.5 w-3.5 mr-1.5" />
                    Print
                  </Button>
                  <a
                    href={`/api/purchase-orders/${id}/pdf?download=1`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm font-medium hover:bg-muted transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download PDF
                  </a>
                  <Button variant="outline" size="sm" onClick={() => setEmailModalOpen(true)}>
                    <Mail className="h-3.5 w-3.5 mr-1.5" />
                    Email
                  </Button>
                  {canEdit && (
                    <Link to={`/purchase-orders/${id}/edit`}>
                      <Button variant="outline" size="sm">
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        Edit
                      </Button>
                    </Link>
                  )}
                  {(po.status === 'received' || po.status === 'returned') && (
                    <Link to={`/purchase-orders/${id}/return`}>
                      <Button variant="outline" size="sm">
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                        Create Return
                      </Button>
                    </Link>
                  )}
                  {isAdmin &&
                    canRecordPayment &&
                    balanceDue > 0 &&
                    balanceDue <= WRITE_OFF_CAP_CENTS && (
                      <Button variant="outline" size="sm" onClick={() => setWriteOffOpen(true)}>
                        <Eraser className="h-3.5 w-3.5 mr-1.5" />
                        Write off balance
                      </Button>
                    )}
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                      onClick={() => setDeletePOOpen(true)}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>

              {/* ── Lifecycle buttons ── */}
              {(po.status === 'draft' || po.status === 'sent') && (
                <div className="mt-4 pt-4 border-t flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide mr-1">
                    Actions:
                  </span>
                  {po.status === 'draft' && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={lifecycleLoading !== null}
                      onClick={() =>
                        setConfirmLifecycle({
                          action: 'send',
                          label: 'Mark as Sent',
                          message: `Mark PO ${po.number} as sent to ${supplier.name}?`,
                        })
                      }
                    >
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                      Mark Sent
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-700 hover:text-green-700 border-green-200 hover:border-green-400 hover:bg-green-50 dark:text-green-400 dark:border-green-900 dark:hover:bg-green-950/30"
                    disabled={lifecycleLoading !== null}
                    onClick={() =>
                      setConfirmLifecycle({
                        action: 'receive',
                        label: 'Mark as Received',
                        message: `Mark PO ${po.number} as received? This will increment stock for all linked inventory items.`,
                      })
                    }
                  >
                    <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                    Mark Received
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
                    disabled={lifecycleLoading !== null}
                    onClick={() =>
                      setConfirmLifecycle({
                        action: 'cancel',
                        label: 'Cancel PO',
                        message: `Cancel PO ${po.number}? This cannot be undone.`,
                      })
                    }
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1.5" />
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* ── Line items + totals ── */}
          <Card>
            <div className="px-4 py-3 border-b bg-muted/30">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Line Items
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/20">
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground w-8">
                      #
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                      Description
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-20">
                      Qty
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                      Unit Price
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                      Line Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr
                      key={item.id}
                      className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                    >
                      <td className="px-3 py-2.5 text-xs text-muted-foreground text-center">
                        {i + 1}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-sm font-medium">
                          {item.item_id != null ? (
                            <Link
                              to={`/items/${item.item_id}`}
                              className="inline-block hover:underline hover:text-primary transition-colors"
                            >
                              {item.item_name ?? item.description}
                            </Link>
                          ) : (
                            (item.item_name ?? item.description)
                          )}
                        </div>
                        {item.sqft != null && (
                          <div className="text-xs text-muted-foreground">({item.sqft} sq ft)</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm">{item.qty}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm">
                        {formatCents(item.unit_price)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                        {formatCents(item.line_total)}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-sm text-muted-foreground"
                      >
                        No line items.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals block */}
            <div className="px-4 py-4 border-t bg-muted/10">
              <div className="ml-auto max-w-xs space-y-1.5">
                {po.write_off > 0 && (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="tabular-nums">{formatCents(po.subtotal)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Adjustment</span>
                      <span className="tabular-nums text-amber-700 dark:text-amber-400">
                        −{formatCents(po.write_off)}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between pt-2 border-t font-semibold">
                  <span>PO Total</span>
                  <span className="tabular-nums text-base">{formatCents(po.total)}</span>
                </div>
                {po.amount_paid > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Amount Paid</span>
                    <span className="tabular-nums text-green-700 dark:text-green-400">
                      {formatCents(po.amount_paid)}
                    </span>
                  </div>
                )}
                {(po.status === 'sent' || po.status === 'received' || po.status === 'returned') && (
                  <div className="flex items-center justify-between text-sm font-medium pt-1 border-t">
                    <span>Balance Due</span>
                    <span
                      className={`tabular-nums ${balanceDue > 0 ? 'text-destructive' : 'text-green-700 dark:text-green-400'}`}
                    >
                      {formatCents(Math.max(balanceDue, 0))}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* ── Payments section ── */}
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Payments
                </h2>
                {canRecordPayment && (
                  <Button size="sm" onClick={() => setRecordPaymentOpen(true)}>
                    <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                    Record Payment
                  </Button>
                )}
              </div>

              {payments.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No payments recorded yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Date
                        </th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                          Amount
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Notes
                        </th>
                        {isAdmin && <th className="px-3 py-2.5 w-20" />}
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((pmt) => (
                        <tr
                          key={pmt.id}
                          className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                        >
                          <td className="px-3 py-2.5 tabular-nums text-sm">
                            {formatDate(pmt.date)}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium text-green-700 dark:text-green-400">
                            {formatCents(pmt.amount)}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-muted-foreground">
                            {pmt.notes ?? '—'}
                          </td>
                          {isAdmin && (
                            <td className="px-3 py-2.5">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() => setEditPayment(pmt)}
                                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                  title="Edit payment"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeletePaymentId(pmt.id)}
                                  className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                  title="Delete payment"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right sidebar (40%): purchase returns, attachments / notes */}
        <aside className="lg:col-span-2 space-y-6">
          {/* ── Purchase Returns section ── */}
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b bg-muted/30">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Purchase Returns
                </h2>
              </div>
              {!purchase_returns?.length ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No purchase returns against this PO.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Number
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Status
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Date
                        </th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchase_returns.map((pr) => (
                        <tr
                          key={pr.id}
                          className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                        >
                          <td className="px-3 py-2.5">
                            <Link
                              to={`/purchase-returns/${pr.id}`}
                              className="font-mono text-sm font-medium text-primary hover:underline"
                            >
                              {pr.number}
                            </Link>
                          </td>
                          <td className="px-3 py-2.5">
                            <Badge variant={pr.status === 'refunded' ? 'green' : 'amber'}>
                              {pr.status.charAt(0).toUpperCase() + pr.status.slice(1)}
                            </Badge>
                          </td>
                          <td className="px-3 py-2.5 text-sm text-muted-foreground tabular-nums">
                            {formatDate(pr.date)}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                            {formatCents(pr.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Attachments ── */}
          <AttachmentsPanel module="purchase_order" recordId={Number(id)} />
        </aside>
      </div>

      {/* ── Modals ── */}

      {linkEstimateOpen && (
        <LinkEstimateToPoModal
          poId={po.id}
          currentEstimate={source_estimate}
          onClose={() => setLinkEstimateOpen(false)}
          onLinked={invalidate}
        />
      )}

      {recordPaymentOpen && (
        <RecordPoPaymentModal
          poId={po.id}
          supplierId={po.supplier_id}
          balanceDue={balanceDue}
          onClose={() => setRecordPaymentOpen(false)}
          onSaved={invalidate}
        />
      )}

      {editPayment && (
        <EditPoPaymentModal
          payment={editPayment}
          supplierId={po.supplier_id}
          onClose={() => setEditPayment(null)}
          onSaved={invalidate}
        />
      )}

      {deletePaymentId !== null && (
        <ConfirmDialog
          title="Delete Payment"
          message="Are you sure you want to delete this payment? This action cannot be undone."
          confirmLabel="Delete"
          confirmVariant="destructive"
          onConfirm={() => handleDeletePayment(deletePaymentId)}
          onCancel={() => setDeletePaymentId(null)}
          loading={deletingPayment}
        />
      )}

      {deletePOOpen && (
        <ConfirmDialog
          title="Delete Purchase Order"
          message={`Are you sure you want to delete PO ${po.number}? This will reverse any stock changes and remove associated payments.`}
          confirmLabel="Delete"
          confirmVariant="destructive"
          onConfirm={handleDeletePO}
          onCancel={() => setDeletePOOpen(false)}
          loading={deletingPO}
        />
      )}

      {writeOffOpen && (
        <ConfirmDialog
          title="Write off balance"
          message={`Write off remaining ${formatCents(balanceDue)} and clear this PO's balance? This records an adjustment, not a cash payment.`}
          confirmLabel="Write Off"
          confirmVariant="default"
          onConfirm={handleWriteOff}
          onCancel={() => setWriteOffOpen(false)}
          loading={writingOff}
        />
      )}

      {confirmLifecycle && (
        <ConfirmDialog
          title={confirmLifecycle.label}
          message={confirmLifecycle.message}
          confirmLabel={confirmLifecycle.label}
          confirmVariant={confirmLifecycle.action === 'cancel' ? 'destructive' : 'default'}
          onConfirm={() => handleLifecycle(confirmLifecycle.action)}
          onCancel={() => setConfirmLifecycle(null)}
          loading={lifecycleLoading !== null}
        />
      )}

      <EmailPdfModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        endpoint={`/api/purchase-orders/${id}/email`}
        defaultTo={supplier.email ?? ''}
        defaultSubject={`Purchase Order ${po.number}`}
        pdfHref={`/api/purchase-orders/${id}/pdf`}
      />
    </div>
  );
}
